'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useCartStore } from '@/ui/store/cart';
import { useForm, SubmitHandler } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { createPortal } from 'react-dom';
import { NOTIFY_ME_PORTAL_TARGET_ID } from '@/utils/constants';
import Button from '../../shared/Button';
import TextInput from '../../shared/inputs/TextInput';

type FormValues = {
  email: string;
  agree: boolean;
};

function NotifyMeModal() {
  const { showNotifyMe, setShowNotifyMe } = useCartStore();
  const isHydrated = useIsHydrated();
  const t = useTranslations('NotifyMe');

  const dialogPanelRef = useRef<HTMLDivElement>(null);

  const validationSchema = yup.object().shape({
    email: yup.string().email(t('Please enter valid email')).required(t('Email is required')),
    agree: yup.boolean().oneOf([true], t('You must agree to the terms')),
  });

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    // @ts-ignore because of the bug between yup and react-hook-form
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const closeModal = () => {
    setShowNotifyMe(false, null, null);
    setErrorMessage(null);
    setSuccess(false);
    reset();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setErrorMessage(null);
    setSuccess(false);
    setLoading(true);

    try {
      const currentUrl = globalThis.location.href;
      const parsedUrl = new URL(currentUrl);
      const path = parsedUrl.pathname;
      const language = path.split('/')?.[1] || undefined;
      const urlParams = new URLSearchParams(parsedUrl.search);

      const sku = showNotifyMe.productVariant?.sku || urlParams.get('sku') || undefined;

      const response = await axios.post(apiPaths.communication.backInStock, {
        email: data.email,
        language,
        nukiProductId: sku,
        pageUrl: currentUrl,
      });

      if (response.status === 200) {
        setSuccess(true);
        reset();
      } else {
        setErrorMessage(t('Failed to register for notifications'));
        throw new Error('Submission failed');
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setErrorMessage(t('Failed to register for notifications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showNotifyMe?.show) {
      setTimeout(() => {
        setFocus('email');
      }, 20);
    }
  }, [showNotifyMe?.show]);

  if (!showNotifyMe || !isHydrated || typeof document === 'undefined') {
    return null;
  }

  // In case chooser is open, open the dialog as a children of the chooser overlay to avoid focus trap otherwise append to the root
  const portalTarget = document.getElementById(NOTIFY_ME_PORTAL_TARGET_ID) || document.body;

  return createPortal(
    <Dialog
      open={showNotifyMe.show}
      onClose={closeModal}
      initialFocus={dialogPanelRef}
      className="relative z-60"
    >
      <div className="fixed inset-0 bg-neutral-50 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            ref={dialogPanelRef}
            className="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all sm:w-full sm:max-w-md sm:p-8"
            tabIndex={-1}
          >
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-1 top-1 p-1 text-neutral-20 hover:text-neutral-30 focus:outline-none"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">{t('Close')}</span>
            </button>

            <Dialog.Title as="h3" className="text-lg font-semibold text-neutral-90">
              {showNotifyMe.bundle
                ? t('The bundle variant is currently not available', {
                    variantName: showNotifyMe.bundle?.name || t('Bundle'),
                  })
                : t('The product variant is currently not available', {
                    variantName: showNotifyMe.productVariant?.name || t('Product'),
                  })}
            </Dialog.Title>
            <p className="mt-2">{t('Please fill in the form below')}</p>
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
              <label htmlFor="email" className="block text-sm font-bold">
                {t('Email address')}
                <span className="ml-1 text-primary-100">*</span>
              </label>
              <TextInput
                id="email"
                register={register}
                errors={undefined}
                srLabel={t('Enter your email')}
                placeholder={t('Enter your email')}
                className="block w-full py-1"
              />
              <p className="h-4 text-sm text-red">{errors.email?.message ?? ''}</p>
              <div className="mt-2 flex">
                <input
                  type="checkbox"
                  id="agree"
                  {...register('agree', { required: t('You must agree to the terms') })}
                  className="h-4 w-4 rounded border-neutral-30 text-primary-60 focus:ring-primary-50"
                />
                <label htmlFor="agree" className="ml-2 block text-sm">
                  {t('I agree to receive an email notification')}
                </label>
              </div>
              <p className="h-4 text-sm text-red">{errors.agree?.message ?? ''}</p>

              <Button type="submit" className="mt-2 w-full rounded-sm py-2" loading={loading}>
                {t('Notify me')}
              </Button>

              {errorMessage && <p className="mb-4 px-4 py-2 text-sm text-red">{errorMessage}</p>}
              {success && (
                <p className="mb-4 mt-2 rounded-sm border border-green bg-green/10 p-2 text-sm">
                  <span className="font-bold">{t('You are on the list')}</span>
                  <span>{t('As soon as the product is back in stock')}</span>
                </p>
              )}

              <p className="mt-2 text-center text-sm text-neutral-50">
                {t.rich('We will only use your email for this notification', {
                  link: (children) => (
                    <a href={t('TPP Link')} className="underline">
                      {children}
                    </a>
                  ),
                })}
              </p>
            </form>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>,
    portalTarget,
  );
}

export default NotifyMeModal;
